import { QueryClient, useQueryClient } from '@tanstack/react-query';
import type { PostingRequirementsDef } from './requirements-key';
import { VonqPostingRequirementAutocompleteResponse } from 'api-clients/monolith/models/VonqPostingRequirementAutocompleteResponse';

import { LoadedOptions } from './display-rules';

import { queryKeys } from '@/query-keys';

/**
 * Reads lazy-loaded autocomplete option metadata for all fields in a single
 * Vonq contract directly from the React Query cache.
 *
 * The cache is populated by `VonqAutocompleteInput` when it mounts for each
 * lazy-load field. By the time the user selects an option (which triggers a
 * display-rule re-evaluation), the cache is guaranteed to have the data.
 *
 * This is a snapshot read — no query subscriptions are created. Reactivity
 * comes from the `watch()` calls on `vonqPostingRequirements` in the consuming
 * components; those re-renders pick up the latest cache state here.
 *
 * Can also be called outside of React components (e.g. form submission
 * handlers) by passing a `QueryClient` instance directly.
 */
export function buildLoadedOptionsFromCache(
  contract: PostingRequirementsDef,
  queryClient: QueryClient,
): LoadedOptions {
  const loadedOptions: LoadedOptions = {};
  for (const field of contract.posting_requirements) {
    if (field.autocomplete !== null) {
      const cached =
        queryClient.getQueryData<VonqPostingRequirementAutocompleteResponse>(
          queryKeys.jobPosting.vonqAutocomplete.lazy(contract.key, field.name),
        );
      if (cached?.options?.length) {
        loadedOptions[field.name] = cached.options.map(opt => ({
          key: opt.key,
          show: Array.isArray(opt.show) ? (opt.show as string[]) : null,
        }));
      }
    }
  }
  return loadedOptions;
}

/** React hook wrapper around `buildLoadedOptionsFromCache`. */
export function useAutocompleteOptions(
  contract: PostingRequirementsDef | undefined,
): LoadedOptions {
  const queryClient = useQueryClient();
  if (!contract) return {};
  return buildLoadedOptionsFromCache(contract, queryClient);
}
